/**
 * Judged test play (W2-02; ARCHITECTURE.md §7.5, DESIGN.md §4.9, rpe-format.md F§11.6, rendering.md R§5.5).
 * Implemented from the documented behaviour (no Phira source).
 *
 * Input model (there is no touch screen): any letter key or the left mouse button is a TOUCH.
 *  - A touch press judges the EARLIEST unjudged Tap or Hold head from `engine.notesInWindow` around the press,
 *    regardless of position. Taps: Perfect within ±80 ms, Good within ±160 ms, Bad within ±220 ms. Hold heads are
 *    judged within the Good window only (Perfect or Good, never Bad): a press 160-220 ms away does not take the hold,
 *    which becomes a Miss when its Good window closes. Each press judges one note, so a chord needs one key per note.
 *  - A held touch (or a press shortly before) hits Drags when they reach the line (Perfect, Good window).
 *  - Flicks need a touch plus a mouse movement above a threshold, or the dedicated flick key: the earliest unjudged
 *    Flick within the Good window is hit (Perfect).
 *  - A hit Hold stays alive while any touch is held; releasing every touch before `end - 0.05 s` misses it.
 *  - Notes whose window passed unjudged are Misses.
 * The session is the engine's `JudgeView` (visibility, hold drawing, hit effects and the judged HUD read it); hits
 * report the sound to play (`audio.playHitsound`), Perfect and Good only (F§11.7). Notes before the session start
 * count as already played (judged at their time, like autoplay) and stay out of the results.
 */

import type { JudgeGrade, JudgeView } from '../../core/engine/types';
import type { AssetId, LineId, NoteId, NoteKind } from '../../core/model/types';
import type { Sec } from '../../core/time/types';

/** Phira's judgement windows (seconds). */
export const LIMIT_PERFECT = 0.08;
export const LIMIT_GOOD = 0.16;
export const LIMIT_BAD = 0.22;
/** A hold released this close to its end still counts as held to the end. */
export const HOLD_RELEASE_TOLERANCE = 0.05;
/** Width of one early/late histogram bar (seconds). */
export const HISTOGRAM_BIN = 0.02;
/** |mean offset| above this suggests a timing or latency problem (seconds). */
export const MEAN_HINT_THRESHOLD = 0.02;

export type HitSound =
  | { readonly kind: 'skin'; readonly name: 'click' | 'drag' | 'flick' }
  | { readonly kind: 'asset'; readonly asset: AssetId };

/** What the judge needs to know about a real (non-fake) note. */
export interface JudgeNote {
  readonly id: NoteId;
  readonly kind: NoteKind;
  readonly line: LineId;
  /** Hit time (chart seconds). */
  readonly time: Sec;
  /** End time (holds; = time otherwise). */
  readonly end: Sec;
  readonly sound: HitSound;
}

/** Notes source: the engine (production) or a synthetic list (tests). */
export interface JudgeSource {
  /** Non-fake notes with time in [t0, t1], sorted by time then id (Engine.notesInWindow). */
  notesInWindow(t0: Sec, t1: Sec): readonly NoteId[];
  note(id: NoteId): JudgeNote | null;
}

export interface JudgeStatus {
  readonly grade: JudgeGrade;
  readonly at: Sec;
  readonly holding: boolean;
}

/** Something that happened during an input or a frame (hits play their sound). */
export interface JudgeEvent {
  readonly note: NoteId;
  readonly grade: JudgeGrade;
  /** Input time minus note time (seconds); null for judgements without a timing (drags, misses, hold ends). */
  readonly offset: number | null;
  /** Sound to play now (Perfect / Good hits), else null. */
  readonly sound: HitSound | null;
  readonly kind: 'hit' | 'miss' | 'holdEnd' | 'holdBroken';
}

export interface HistogramBin {
  /** Bin range in seconds [from, to). */
  readonly from: number;
  readonly to: number;
  readonly notes: readonly NoteId[];
}

export interface JudgeResults {
  /** Notes of the session (time >= start). */
  readonly total: number;
  readonly perfect: number;
  readonly good: number;
  readonly bad: number;
  readonly miss: number;
  /** Notes not judged yet (session ended early). */
  readonly pending: number;
  readonly maxCombo: number;
  /** (P + 0.65 G) / judged notes, 0..1 (1 before any judgement). */
  readonly accuracy: number;
  /** Phigros score: round(900000 (P + 0.65 G) / N + 100000 maxCombo / N). */
  readonly score: number;
  readonly early: number;
  readonly late: number;
  /** Mean and standard deviation of the timed hits (seconds), null without any. */
  readonly meanOffset: number | null;
  readonly stdOffset: number | null;
  readonly histogram: readonly HistogramBin[];
  /** Timed hits with their note (results list and jump targets). */
  readonly offsets: ReadonlyArray<{ readonly note: NoteId; readonly offset: number }>;
}

interface NoteRecord {
  status: JudgeStatus | null;
  /** The judgement that ends the note for the results (holds: their final outcome). */
  final: JudgeGrade | null;
  offset: number | null;
}

/** Grade of a timed hit by |offset|; null outside the window `limit`. */
export function gradeFor(offset: number, limit: number = LIMIT_BAD): JudgeGrade | null {
  const a = Math.abs(offset);
  if (a > limit + 1e-9) return null;
  if (a <= LIMIT_PERFECT + 1e-9) return 'perfect';
  if (a <= LIMIT_GOOD + 1e-9) return 'good';
  return 'bad';
}

/** Index of the first note with time >= t in a sorted array. */
function lowerBound(times: Float64Array, n: number, t: number): number {
  let lo = 0;
  let hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if ((times[mid] ?? Infinity) < t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

export class JudgeSession implements JudgeView {
  private readonly ids: NoteId[] = [];
  private readonly notes: JudgeNote[] = [];
  private times = new Float64Array(0);
  private readonly index = new Map<NoteId, number>();
  private readonly records = new Map<NoteId, NoteRecord>();
  /** First note index whose window may still be open (everything before is final). */
  private doneCursor = 0;
  /** First index of the session's notes (time >= start). */
  private sessionFrom = 0;
  private readonly held = new Set<string>();
  private lastPressAt = -Infinity;
  private readonly holding = new Set<NoteId>();
  private startSec = 0;
  private readonly source: JudgeSource;

  constructor(source: JudgeSource) {
    this.source = source;
  }

  /** (Re)starts the session at chart time `start`: notes before it count as played (not in the results). */
  start(start: Sec, horizon: Sec): void {
    this.ids.length = 0;
    this.notes.length = 0;
    this.index.clear();
    this.records.clear();
    this.held.clear();
    this.holding.clear();
    this.lastPressAt = -Infinity;
    this.startSec = start;
    const all = this.source.notesInWindow(-Infinity, Math.max(horizon, start) + 1);
    for (const id of all) {
      const n = this.source.note(id);
      if (n === null) continue;
      this.index.set(id, this.notes.length);
      this.ids.push(id);
      this.notes.push(n);
    }
    this.times = new Float64Array(this.notes.length);
    this.notes.forEach((n, i) => {
      this.times[i] = n.time;
    });
    this.sessionFrom = lowerBound(this.times, this.notes.length, start);
    for (let i = 0; i < this.sessionFrom; i++) {
      const n = this.notes[i];
      if (n === undefined) continue;
      // Played before the start: judged Perfect at its time, as autoplay would (not counted in the results).
      this.records.set(n.id, {
        status: { grade: 'perfect', at: n.time, holding: false },
        final: 'perfect',
        offset: null,
      });
    }
    this.doneCursor = this.sessionFrom;
  }

  get sessionStart(): Sec {
    return this.startSec;
  }

  /** JudgeView: the status of a note (null = not judged yet). */
  status(note: NoteId): JudgeStatus | null {
    return this.records.get(note)?.status ?? null;
  }

  /** Whether any touch is currently held. */
  get touching(): boolean {
    return this.held.size > 0;
  }

  private record(id: NoteId): NoteRecord {
    let r = this.records.get(id);
    if (r === undefined) {
      r = { status: null, final: null, offset: null };
      this.records.set(id, r);
    }
    return r;
  }

  private hitEvent(n: JudgeNote, grade: JudgeGrade, offset: number | null, kind: JudgeEvent['kind']): JudgeEvent {
    const sound = grade === 'perfect' || grade === 'good' ? n.sound : null;
    return { note: n.id, grade, offset, sound: kind === 'hit' ? sound : null, kind };
  }

  /** A touch begins at chart time t (key code or 'mouse'). */
  press(t: Sec, touch: string): JudgeEvent[] {
    const fresh = !this.held.has(touch);
    this.held.add(touch);
    if (!fresh) return [];
    this.lastPressAt = t;
    const out: JudgeEvent[] = [];
    // Earliest unjudged Tap / Hold head in the window.
    for (const id of this.source.notesInWindow(t - LIMIT_BAD, t + LIMIT_BAD)) {
      const i = this.index.get(id);
      if (i === undefined || i < this.sessionFrom) continue;
      const n = this.notes[i];
      if (n === undefined || (n.kind !== 'tap' && n.kind !== 'hold')) continue;
      if (this.records.get(id)?.status != null) continue;
      const offset = t - n.time;
      const grade = gradeFor(offset, n.kind === 'hold' ? LIMIT_GOOD : LIMIT_BAD);
      if (grade === null) continue;
      const r = this.record(id);
      if (n.kind === 'hold') {
        // Graded over the Good window (see the module comment): Perfect or Good, and the hold is alive.
        r.status = { grade, at: t, holding: true };
        this.holding.add(id);
      } else {
        r.status = { grade, at: t, holding: false };
        r.final = grade;
      }
      r.offset = offset;
      out.push(this.hitEvent(n, grade, offset, 'hit'));
      break;
    }
    // A press also catches drags that are due now (late touches).
    out.push(...this.catchDrags(t));
    return out;
  }

  /** A touch ends at chart time t. */
  release(t: Sec, touch: string): JudgeEvent[] {
    if (!this.held.delete(touch)) return [];
    if (this.held.size > 0) return [];
    const out: JudgeEvent[] = [];
    for (const id of [...this.holding]) {
      const i = this.index.get(id);
      const n = i === undefined ? undefined : this.notes[i];
      const r = this.records.get(id);
      if (n === undefined || r?.status == null) continue;
      this.holding.delete(id);
      if (t >= n.end - HOLD_RELEASE_TOLERANCE) {
        r.status = { grade: r.status.grade, at: r.status.at, holding: false };
        r.final = r.status.grade;
        out.push(this.hitEvent(n, r.status.grade, null, 'holdEnd'));
      } else {
        r.status = { grade: 'miss', at: t, holding: false };
        r.final = 'miss';
        out.push(this.hitEvent(n, 'miss', null, 'holdBroken'));
      }
    }
    return out;
  }

  /** A flick gesture (touch + mouse movement, or the flick key) at chart time t. */
  flick(t: Sec): JudgeEvent[] {
    for (const id of this.source.notesInWindow(t - LIMIT_GOOD, t + LIMIT_GOOD)) {
      const i = this.index.get(id);
      if (i === undefined || i < this.sessionFrom) continue;
      const n = this.notes[i];
      if (n?.kind !== 'flick' || this.records.get(id)?.status != null) continue;
      const offset = t - n.time;
      const r = this.record(id);
      // An early flick is judged when the note arrives; a late one at once.
      r.status = { grade: 'perfect', at: Math.max(t, n.time), holding: false };
      r.final = 'perfect';
      r.offset = offset;
      return [this.hitEvent(n, 'perfect', offset, 'hit')];
    }
    return [];
  }

  private catchDrags(t: Sec): JudgeEvent[] {
    const out: JudgeEvent[] = [];
    const end = lowerBound(this.times, this.notes.length, t + 1e-9);
    for (let i = this.doneCursor; i < end; i++) {
      const n = this.notes[i];
      if (n?.kind !== 'drag') continue;
      if (this.records.get(n.id)?.status != null) continue;
      if (t > n.time + LIMIT_GOOD) continue;
      const touched = this.held.size > 0 || this.lastPressAt >= n.time - LIMIT_GOOD;
      if (!touched) continue;
      const r = this.record(n.id);
      r.status = { grade: 'perfect', at: Math.max(t, n.time), holding: false };
      r.final = 'perfect';
      out.push(this.hitEvent(n, 'perfect', null, 'hit'));
    }
    return out;
  }

  /** Advances the session to chart time t: drags reached while touching, finished holds, misses. */
  update(t: Sec): JudgeEvent[] {
    const out = this.catchDrags(t);
    for (const id of [...this.holding]) {
      const i = this.index.get(id);
      const n = i === undefined ? undefined : this.notes[i];
      const r = this.records.get(id);
      if (n === undefined || r?.status == null) continue;
      if (t >= n.end) {
        this.holding.delete(id);
        r.status = { grade: r.status.grade, at: r.status.at, holding: false };
        r.final = r.status.grade;
        out.push(this.hitEvent(n, r.status.grade, null, 'holdEnd'));
      }
    }
    // Misses: windows that closed without a judgement (in time order).
    while (this.doneCursor < this.notes.length) {
      const n = this.notes[this.doneCursor];
      if (n === undefined) break;
      const limit = n.kind === 'tap' ? LIMIT_BAD : LIMIT_GOOD;
      if (t <= n.time + limit) break;
      const r = this.record(n.id);
      if (r.status === null) {
        r.status = { grade: 'miss', at: n.time + limit, holding: false };
        r.final = 'miss';
        out.push(this.hitEvent(n, 'miss', null, 'miss'));
      }
      this.doneCursor++;
    }
    return out;
  }

  /** Notes of the session in time order (results, jump targets). */
  sessionNotes(): readonly JudgeNote[] {
    return this.notes.slice(this.sessionFrom);
  }

  results(): JudgeResults {
    const session = this.sessionNotes();
    let perfect = 0;
    let good = 0;
    let bad = 0;
    let miss = 0;
    let pending = 0;
    let combo = 0;
    let maxCombo = 0;
    let early = 0;
    let late = 0;
    const offsets: Array<{ note: NoteId; offset: number }> = [];
    // Combo in commit order: holds commit at their end, other notes at their judgement.
    const commits: Array<{ at: number; grade: JudgeGrade }> = [];
    for (const n of session) {
      const r = this.records.get(n.id);
      const g = r?.final ?? null;
      if (r === undefined || g === null) {
        pending++;
        continue;
      }
      if (g === 'perfect') perfect++;
      else if (g === 'good') good++;
      else if (g === 'bad') bad++;
      else miss++;
      const at =
        r.status === null ? n.time : n.kind === 'hold' && g !== 'miss' ? Math.max(r.status.at, n.end) : r.status.at;
      commits.push({ at, grade: g });
      if (r.offset !== null && g !== 'miss') {
        offsets.push({ note: n.id, offset: r.offset });
        if (g !== 'perfect') {
          if (r.offset < 0) early++;
          else late++;
        }
      }
    }
    commits.sort((a, b) => a.at - b.at);
    for (const c of commits) {
      if (c.grade === 'perfect' || c.grade === 'good') {
        combo++;
        if (combo > maxCombo) maxCombo = combo;
      } else combo = 0;
    }
    const judged = perfect + good + bad + miss;
    const weighted = perfect + 0.65 * good;
    const total = session.length;
    let mean: number | null = null;
    let std: number | null = null;
    if (offsets.length > 0) {
      mean = offsets.reduce((s, o) => s + o.offset, 0) / offsets.length;
      const m = mean;
      std = Math.sqrt(offsets.reduce((s, o) => s + (o.offset - m) ** 2, 0) / offsets.length);
    }
    return {
      total,
      perfect,
      good,
      bad,
      miss,
      pending,
      maxCombo,
      accuracy: judged === 0 ? 1 : weighted / judged,
      score: total === 0 ? 0 : Math.round((9e5 * weighted) / total + (1e5 * maxCombo) / total),
      early,
      late,
      meanOffset: mean,
      stdOffset: std,
      histogram: histogram(offsets),
      offsets,
    };
  }
}

/** Early/late histogram: bins of HISTOGRAM_BIN seconds over the Bad window, each with its notes. */
export function histogram(offsets: ReadonlyArray<{ readonly note: NoteId; readonly offset: number }>): HistogramBin[] {
  const count = Math.round((2 * LIMIT_BAD) / HISTOGRAM_BIN);
  const bins: Array<{ from: number; to: number; notes: NoteId[] }> = [];
  for (let i = 0; i < count; i++) {
    const from = -LIMIT_BAD + i * HISTOGRAM_BIN;
    bins.push({ from: round6(from), to: round6(from + HISTOGRAM_BIN), notes: [] });
  }
  for (const o of offsets) {
    let i = Math.floor((o.offset + LIMIT_BAD) / HISTOGRAM_BIN + 1e-9);
    i = Math.max(0, Math.min(count - 1, i));
    bins[i]?.notes.push(o.note);
  }
  return bins;
}

function round6(v: number): number {
  return Math.round(v * 1e6) / 1e6;
}

/** True when the mean offset suggests a timing / latency problem (the results show the hint). */
export function needsTimingHint(r: Pick<JudgeResults, 'meanOffset' | 'offsets'>): boolean {
  return r.meanOffset !== null && r.offsets.length >= 3 && Math.abs(r.meanOffset) > MEAN_HINT_THRESHOLD;
}

/** Default sound of a note kind (Hold heads use click; F§11.7, S§5). */
export function defaultSound(kind: NoteKind): HitSound {
  switch (kind) {
    case 'drag':
      return { kind: 'skin', name: 'drag' };
    case 'flick':
      return { kind: 'skin', name: 'flick' };
    default:
      return { kind: 'skin', name: 'click' };
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Flick detection from mouse movement
// ---------------------------------------------------------------------------------------------------------------

/** Pointer speed (CSS px per ms) that counts as a flick while a touch is held. */
export const FLICK_SPEED = 0.6;
/** Speed under which a new flick may start again. */
export const FLICK_REARM_SPEED = 0.2;

/** Turns pointer movement into flick gestures (one per swipe). */
export class FlickDetector {
  private last: { x: number; y: number; ms: number } | null = null;
  private armed = true;

  /** Feeds a pointer position; returns true when a flick happens now (a touch must be held). */
  move(x: number, y: number, ms: number, touching: boolean): boolean {
    const prev = this.last;
    this.last = { x, y, ms };
    if (prev === null) return false;
    const dt = Math.max(1, ms - prev.ms);
    const speed = Math.hypot(x - prev.x, y - prev.y) / dt;
    if (speed < FLICK_REARM_SPEED) this.armed = true;
    if (!touching || !this.armed || speed < FLICK_SPEED) return false;
    this.armed = false;
    return true;
  }

  reset(): void {
    this.last = null;
    this.armed = true;
  }
}
